import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  TableChart as TableChartIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { DocumentDataSetsPanel } from '../../../components/appraisals/construction/DocumentDataSetsPanel';

interface DocumentData {
  document: {
    id: number;
    appraisalId: number;
    constructionId: number;
    documentIdentifier: string;
    description?: string;
    primaryWorksheetName: string;
    alternativeWorksheetNames: string[];
    status: string;
    createdAt: string;
    updatedAt: string;
    worksheetSavedAt: string;
  };
  construction: {
    id: number;
    name: string;
    fileName: string;
    blobName: string;
    fileSize: number;
    worksheetCount: number;
    status: string;
    createdAt: string;
  };
  appraisal: {
    id: number;
    name: string;
  };
  worksheetInfo: {
    primaryWorksheet: string;
    alternativeWorksheets: string[];
    savedAt: string;
  };
  dataSets: DataSet[];
  summary: {
    totalDataSets: number;
    dataSetStatuses: Record<string, number>;
  };
}

interface DataSet {
  id: number;
  datasetName: string;
  datasetIdentifier: string;
  worksheetName: string;
  startCell: string;
  endCell: string;
  status: string;
  rowCount: number;
  columnCount: number;
  createdAt: string;
  updatedAt: string;
}

export const XlsxDocumentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch document details
  const fetchDocumentDetails = useCallback(async () => {
    try {
      setError(null);

      // Fetch document details and datasets concurrently
      const [documentResponse, datasetsResponse] = await Promise.all([
        authenticatedFetch(`/api/appraisals/admin/documents/${documentId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        authenticatedFetch(`/api/appraisals/admin/documents/${documentId}/datasets`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })
      ]);

      if (!documentResponse) {
        throw new Error('No response from server for document details');
      }

      if (!documentResponse.ok) {
        const errorData = await documentResponse.json();
        throw new Error(errorData.message || `Error: ${documentResponse.status}`);
      }

      const documentResult = await documentResponse.json();
      if (!documentResult.success || !documentResult.document) {
        throw new Error(documentResult.message || 'Failed to fetch document details');
      }

      // Parse datasets response
      let datasets: DataSet[] = [];
      let datasetStatuses: Record<string, number> = {};

      if (datasetsResponse && datasetsResponse.ok) {
        const datasetsResult = await datasetsResponse.json();
        if (datasetsResult.success && datasetsResult.datasets) {
          datasets = datasetsResult.datasets.map((dataset: any): DataSet => ({
            id: dataset.id,
            datasetName: dataset.datasetName,
            datasetIdentifier: dataset.datasetIdentifier,
            worksheetName: dataset.worksheetName,
            startCell: dataset.startCell,
            endCell: dataset.endCell,
            status: 'active', // Default status since not provided by API
            rowCount: 0, // Default since not provided by API
            columnCount: 0, // Default since not provided by API
            createdAt: dataset.createdAt,
            updatedAt: dataset.updatedAt,
          }));

          // Calculate status counts
          datasetStatuses = datasets.reduce((acc, dataset) => {
            acc[dataset.status] = (acc[dataset.status] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
        }
      }

      // Transform the API response into the expected DocumentData structure
      const transformedData: DocumentData = {
        document: {
          id: documentResult.document.id,
          appraisalId: 0, // Not available from current API
          constructionId: 0, // Will be populated from construction section
          documentIdentifier: documentResult.document.documentIdentifier,
          description: documentResult.document.documentName,
          primaryWorksheetName: documentResult.document.worksheetName,
          alternativeWorksheetNames: [],
          status: 'identified',
          createdAt: documentResult.document.createdAt,
          updatedAt: documentResult.document.updatedAt,
          worksheetSavedAt: documentResult.document.createdAt,
        },
        construction: {
          id: 0, // Not available from current API
          name: documentResult.document.documentName,
          fileName: documentResult.document.documentName + '.xlsx',
          blobName: documentResult.document.azureBlobPath || '',
          fileSize: 0, // Not available from current API
          worksheetCount: 1, // Not available from current API
          status: 'processed',
          createdAt: documentResult.document.createdAt,
        },
        appraisal: {
          id: 0, // Not available from current API
          name: 'Unknown Appraisal',
        },
        worksheetInfo: {
          primaryWorksheet: documentResult.document.worksheetName,
          alternativeWorksheets: [],
          savedAt: documentResult.document.createdAt,
        },
        dataSets: datasets,
        summary: {
          totalDataSets: datasets.length,
          dataSetStatuses: datasetStatuses,
        },
      };
      setDocumentData(transformedData);
    } catch (err) {
      console.error('Error fetching document details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch document details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedFetch, documentId]);

  useEffect(() => {
    if (documentId) {
      fetchDocumentDetails();
    } else {
      setLoading(false);
      setError('Document ID is required');
    }
  }, [fetchDocumentDetails, documentId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDocumentDetails();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'identified': return 'success';
      case 'processing': return 'warning';
      case 'error': return 'error';
      case 'active': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const handleCreateDataSet = () => {
    navigate(`/admin/deliverables/doc-id-system/documents/${documentId}/data-set/create`);
  };


  const onDataSetUpdate = () => {
    // Refresh the document data when data sets are updated
    fetchDocumentDetails();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!documentData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || 'Document not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <HomeIcon fontSize="inherit" />
          Home
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <AssessmentIcon fontSize="inherit" />
          Deliverables Admin
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables/doc-id-system"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables/doc-id-system');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <TableChartIcon fontSize="inherit" />
          Document Identification System
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AssignmentIcon fontSize="inherit" />
          {documentData.document.description || documentData.construction.name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Document: {documentData.document.documentIdentifier}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {documentData.construction.name} • {documentData.construction.fileName}
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDataSet}
            sx={{ ml: 1 }}
          >
            Create Data Set
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Document Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Document Information
              </Typography>
              <Box mb={2}>
                <Chip 
                  label={documentData.document.status} 
                  color={getStatusColor(documentData.document.status)}
                  size="small"
                />
              </Box>
              <Typography variant="body2" gutterBottom>
                <strong>Identifier:</strong> {documentData.document.documentIdentifier}
              </Typography>
              {documentData.document.description && (
                <Typography variant="body2" gutterBottom>
                  <strong>Description:</strong> {documentData.document.description}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Primary Worksheet:</strong> {documentData.document.primaryWorksheetName}
              </Typography>
              {documentData.document.alternativeWorksheetNames.length > 0 && (
                <Typography variant="body2" gutterBottom>
                  <strong>Alternative Worksheets:</strong> {documentData.document.alternativeWorksheetNames.join(', ')}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Created:</strong> {new Date(documentData.document.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Last Updated:</strong> {new Date(documentData.document.updatedAt).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Construction Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Source File Information
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Construction Name:</strong> {documentData.construction.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>File Name:</strong> {documentData.construction.fileName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>File Size:</strong> {formatFileSize(documentData.construction.fileSize)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Worksheets:</strong> {documentData.construction.worksheetCount}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> <Chip 
                  label={documentData.construction.status} 
                  color={getStatusColor(documentData.construction.status)}
                  size="small"
                />
              </Typography>
              <Typography variant="body2">
                <strong>Uploaded:</strong> {new Date(documentData.construction.createdAt).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>


        {/* Data Sets Management */}
        <Grid item xs={12}>
          <DocumentDataSetsPanel
            documentId={parseInt(documentId!)}
            documentData={documentData}
            onUpdate={onDataSetUpdate}
          />
        </Grid>
      </Grid>
    </Container>
  );
};